import { Box } from '@mui/material';
import { getCompanyMultipleConfig } from 'lib/api/company';
import {
  type AvailableExtensionDocument,
  getDocumentWithExtensionData,
  useCreateFactoringExtension,
  useGetAvailableDocumentsForExtension,
} from 'lib/api/extension/factoring';
import type { FactoringExtensionDetail } from 'lib/api/extension/factoring/types';
import CustomStepper, { StepData } from 'lib/common/Stepper';
import CustomButton from 'lib/designSystem/Button';
import PageHeader from 'lib/designSystem/PageHeader';
import ProcessDialog from 'lib/designSystem/ProcessDialog';
import { isWorkingDay } from 'lib/functions/dates';
import {
  formatDate,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AuthState } from 'services/redux/authSlice';

import StepConfigureExtensions from './components/StepConfigureExtensions/index';
import StepSelectDocuments from './components/StepSelecDocuments';
import type { ExtensionDocument } from './types';

interface NewExtensionsProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCreated?: (extension: FactoringExtensionDetail) => void;
}

const STEPS: StepData[] = [{}, {}];

const calculateDays = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.floor(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
  );
};

const NewExtensions: React.FC<NewExtensionsProps> = ({
  open,
  setOpen,
  onCreated,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<
    AvailableExtensionDocument[]
  >([]);
  const [extensions, setExtensions] = useState<ExtensionDocument[]>([]);
  const [fixedArrearRate, setFixedArrearRate] = useState<number | null>(null);
  const [noIvaComision, setNoIvaComision] = useState<number | null>(null);
  const [canChangeArrearRate, setCanChangeArrearRate] = useState(false);

  const { data, isFetching, refetch } =
    useGetAvailableDocumentsForExtension(dispatch);
  const createMutation = useCreateFactoringExtension(dispatch);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedDocuments([]);
      setExtensions([]);
      void refetch();
    }
  }, [open, refetch]);

  useEffect(() => {
    const run = async () => {
      if (!open) return;
      try {
        const response = await getCompanyMultipleConfig(dispatch, [
          'fixed-arrear-rate-factoringExtension',
          'factoringExtension-no-iva-comision',
        ]);
        const rate = response['fixed-arrear-rate-factoringExtension']
          ? parseFloat(response['fixed-arrear-rate-factoringExtension'])
          : null;
        setFixedArrearRate(rate);
        const noIva = response['factoringExtension-no-iva-comision']
          ? parseFloat(response['factoringExtension-no-iva-comision'])
          : null;
        setNoIvaComision(noIva);
        const canChange = validatePermissions(
          user,
          'factoring_request_edit_arrear_rate',
        );
        setCanChangeArrearRate(rate !== null ? canChange : true);
      } catch (error) {
        console.error(error);
      }
    };

    void run();
  }, [dispatch, open, user]);

  const suggestions = data?.documentsProcessed ?? [];

  const primaryButton = useMemo(() => {
    if (activeStep === 0) {
      return { label: 'Siguiente', disabled: selectedDocuments.length === 0 };
    }
    return {
      label: 'Confirmar',
      disabled: loading || extensions.length === 0,
    };
  }, [activeStep, selectedDocuments.length, loading, extensions.length]);

  const buildExtensions = async (): Promise<ExtensionDocument[]> => {
    const results = await Promise.all(
      selectedDocuments.map(async (doc) => {
        const response = await getDocumentWithExtensionData(dispatch, doc.id);
        if (!response) return null;

        if (!response.paymentDate || !response.startingPaymentDate) return null;

        const paymentDate = String(response.paymentDate);
        const startingPaymentDate = String(response.startingPaymentDate);
        const dueDate = paymentDate ? formatDate(new Date(paymentDate)) : '';

        const days = calculateDays(startingPaymentDate, paymentDate);
        const rate = fixedArrearRate ? fixedArrearRate.toString() : '';
        const rateNumber = fixedArrearRate ?? 0;
        const rateCost =
          (response.documentRemainingAmountNet * rateNumber * days) / 30 / 100;

        return {
          id: response.id,
          folio: response.folio,
          businessIdentifier: response.businessIdentifier,
          businessName: response.businessName,
          stakeholderIdentifier: response.stakeholderIdentifier,
          stakeholderName: response.stakeholderName,
          paymentDate,
          startingPaymentDate,
          documentRemainingAmountNet: response.documentRemainingAmountNet,
          amount: response.amount ?? 0,
          amountConciliated: response.amountNet ?? 0,
          totalRemaining: response.totalRemaining,
          totalArrear: response.totalArrear || 0,
          dueDate,
          rate,
          operationCostFixed: 0,
          operationCostVariable: 0,
          operationCostRateVariable: '',
          rateCost,
          observation: '',
          days,
        } satisfies ExtensionDocument;
      }),
    );

    return results.filter((r): r is ExtensionDocument => r !== null);
  };

  const validParams = () => {
    for (const ext of extensions) {
      if (!ext.dueDate) {
        showAlert(
          dispatch,
          'error',
          `El documento con folio ${ext.folio} no tiene fecha de vencimiento`,
        );
        return false;
      }
      if (!isWorkingDay(new Date(ext.dueDate))) {
        showAlert(
          dispatch,
          'error',
          `El documento con folio ${ext.folio} tiene una fecha no hábil`,
        );
        return false;
      }
      if (ext.rate == null || ext.rate === '') {
        showAlert(
          dispatch,
          'error',
          `El documento con folio ${ext.folio} no tiene una tasa válida`,
        );
        return false;
      }
      if (ext.days <= 0) {
        showAlert(
          dispatch,
          'error',
          `El documento con folio ${ext.folio} tiene días inválidos`,
        );
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validParams()) return;
    setLoading(true);
    try {
      const results = await createMutation.mutateAsync({
        extensions: extensions.map((ext) => ({
          documentId: ext.id,
          rate: parseFloat(ext.rate),
          rateCost: ext.rateCost,
          operationCostRateVariable:
            parseFloat(ext.operationCostRateVariable) || 0,
          startDate: new Date(ext.paymentDate),
          operationCostFixed: Number(ext.operationCostFixed) || 0,
          operationCostVariable: Number(ext.operationCostVariable) || 0,
          dueDate: new Date(ext.dueDate),
          observation: ext.observation || '',
        })),
      });

      const errors = results.filter((r) => !r.success);
      if (errors.length > 0) {
        const docIdToFolio = extensions.reduce<Record<string, string>>(
          (acc, ext) => {
            acc[ext.id] = ext.folio;
            return acc;
          },
          {},
        );
        const errorMessages = errors
          .map(
            (e) =>
              `Folio ${docIdToFolio[e.documentId ?? ''] ?? e.documentId ?? ''}`,
          )
          .join('\n');
        showAlert(
          dispatch,
          'error',
          `Errores al crear prórrogas:\n${errorMessages}`,
        );
      } else {
        showAlert(dispatch, 'success', 'Prórrogas creadas correctamente');
        const first = results[0]?.extension;
        if (first) onCreated?.(first);
        setOpen(false);
      }
    } catch {
      showAlert(dispatch, 'error', 'Error al crear las prórrogas');
    }
    setLoading(false);
  };

  const handlePrimaryAction = async () => {
    if (activeStep === 0) {
      setLoading(true);
      try {
        const hydrated = await buildExtensions();
        setExtensions(hydrated);
        setActiveStep(1);
      } catch {
        showAlert(dispatch, 'error', 'Error al cargar documentos');
      }
      setLoading(false);
      return;
    }
    await handleSubmit();
  };

  const content = (
    <div>
      <Box sx={{ px: 8, mb: 3 }}>
        <CustomStepper
          steps={STEPS}
          activeStep={activeStep}
          orientation="horizontal"
          showStepContent={false}
          showNavigationButtons={false}
          showResetButton={false}
          maxWidth="100%"
        />
      </Box>

      {activeStep === 0 && (
        <StepSelectDocuments
          suggestions={suggestions}
          loading={isFetching || loading}
          selectedDocuments={selectedDocuments}
          onSelectedDocumentsChange={setSelectedDocuments}
        />
      )}

      {activeStep === 1 && (
        <StepConfigureExtensions
          extensions={extensions}
          setExtensions={setExtensions}
          canChangeArrearRate={canChangeArrearRate}
          fixedArrearRate={fixedArrearRate}
          noIvaComision={noIvaComision}
        />
      )}

      <Box
        sx={{
          mt: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 120 }} />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <CustomButton
            color="primary"
            onClick={handlePrimaryAction}
            disabled={primaryButton.disabled}
            loading={loading || createMutation.isPending}
          >
            {primaryButton.label}
          </CustomButton>
        </Box>
        <Box sx={{ minWidth: 120 }} />
      </Box>
    </div>
  );

  return (
    <ProcessDialog.Simple
      open={open}
      onClose={() => setOpen(false)}
      title={
        <PageHeader.TitleSection
          title="Nueva prórroga"
          iconType="add"
          iconVariant="outlined"
          titleVariant="large"
        />
      }
      contentVariant="spacious"
    >
      {content}
    </ProcessDialog.Simple>
  );
};

export default NewExtensions;
